import { REDACTED_RFC } from '@subscriptions/domain/types/SubscriptionsConst';

/**
 * Validated Mexican RFC value object (12-13 characters)
 */
export type Rfc = Readonly<string>;

/**
 * Creates a validated Rfc value object.
 *
 * @param raw - Raw RFC string
 * @returns Validated Rfc
 * @throws Error if RFC format is invalid
 */
export const createRfc = (raw: string): Rfc => {
  const trimmed = raw?.trim().toUpperCase();
  if (!trimmed || trimmed.length < 12 || trimmed.length > 13) {
    throw new Error('RFC must be 12 or 13 characters');
  }
  return trimmed as Rfc;
};

/**
 * RFC shape as the SAT issues it: 3 letters for a company or 4 for an individual, the birth or
 * incorporation date, and the 3-character homoclave.
 *
 * The leading boundary is spelled out as a character class instead of `\b`, because `\b` is
 * defined over `[A-Za-z0-9_]`: an RFC starting with `Ñ` or `&` — both of which the SAT issues —
 * has no word boundary before it and would slip through unredacted.
 */
const RFC_PATTERN = /(^|[^0-9A-ZÑ&])([A-ZÑ&]{3,4}\d{6}[A-Z\d]{2,3})\b/gi;

/**
 * Strips full RFCs out of a backend message.
 *
 * The PAC quotes the taxpayer's RFC back in several of its rejection messages, and those messages
 * travel further than they look: into a query param — which server and CDN access logs capture in
 * full — and into error attributes. Charter §5.1 forbids the full RFC in all of them.
 *
 * Lives in the domain because every layer that handles those messages needs the same guarantee:
 * the mappers redact on the way in, and the link builder redacts again at the boundary that
 * writes to a url.
 *
 * @param message - Backend text that may quote one or more RFCs
 * @returns The same text with every RFC replaced by `[RFC]`
 *
 * @example
 * redactRfc('El regimen 601 no aplica para persona el rfc: XAXX010101000');
 * // 'El regimen 601 no aplica para persona el rfc: [RFC]'
 */
export const redactRfc = (message: string): string => message.replace(RFC_PATTERN, `$1${REDACTED_RFC}`);
